import {
  CategoryChartData,
  CategoryChartSettings,
  CategorySeriesConfig,
  ChartAdapter,
  ChartBuildContext,
  ChartType,
  EChartsOption,
} from "../types";

import {
  buildDataZoom,
  buildLegend,
  buildTooltip,
  mergeCategorySettings,
} from "./shared";

const GRID_SIDE_PADDING = 60;
const AXIS_OFFSET_STEP = 50;
const DEFAULT_COLORS = [
  "#5470C6",
  "#91CC75",
  "#EE6666",
  "#FAC858",
  "#73C0DE",
  "#3BA272",
  "#FC8452",
  "#9A60B4",
  "#EA7CCC",
];

function hexToRgba(hex: string, alpha: number): string | undefined {
  if (!hex.startsWith("#")) {
    return undefined;
  }
  const normalized =
    hex.length === 4
      ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
      : hex;
  const bigint = parseInt(normalized.slice(1), 16);
  if (Number.isNaN(bigint)) {
    return undefined;
  }
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

type CategoryChartType = Extract<ChartType, "bar" | "line">;

const DEFAULT_AXIS_ID = "left";

function pickSeriesType(
  specificType: CategorySeriesConfig["type"],
  chartType: CategoryChartType,
  settings?: Partial<CategoryChartSettings>
) {
  if (specificType) {
    return specificType;
  }

  if (chartType === "line" && settings?.areaDefault) {
    return "line";
  }

  return chartType;
}

interface AxisColorHint {
  color: string;
  isBar: boolean;
}

function resolveAxisConfigs(data: CategoryChartData) {
  const axisConfigs = data.axes || {
    [DEFAULT_AXIS_ID]: {
      id: DEFAULT_AXIS_ID,
      position: "left" as const,
    },
  };

  const entries = Object.values(axisConfigs);
  const idIndexMap = new Map<string, number>();
  const sideOffsetMax: Record<"left" | "right", number> = {
    left: 0,
    right: 0,
  };
  const positionCounters: Record<string, number> = {};

  const yAxis = entries.map((axis, index) => {
    idIndexMap.set(axis.id, index);

    const position =
      axis.position ?? (index === 0 ? ("left" as const) : ("right" as const));
    const counter = positionCounters[position] ?? 0;
    positionCounters[position] = counter + 1;
    const offsetValue =
      position === "left" || position === "right"
        ? counter * AXIS_OFFSET_STEP
        : 0;

    if (
      (position === "left" || position === "right") &&
      offsetValue > sideOffsetMax[position]
    ) {
      sideOffsetMax[position] = offsetValue;
    }

    const axisLine: Record<string, any> = {};
    if (axis.showAxisLine !== undefined) {
      axisLine.show = axis.showAxisLine;
    }
    const splitLine: Record<string, any> = {
      show: false, // 默认关闭分割线
    };

    return {
      id: axis.id,
      type: "value",
      position,
      name: axis.name,
      // 如果未设置 min，默认使用 "dataMin" 以自动根据数据调整起始坐标，避免下方空白
      min: axis.min !== undefined ? axis.min : "dataMin",
      // 如果未设置 max，默认使用 "dataMax" 以自动根据数据调整结束坐标
      max: axis.max !== undefined ? axis.max : "dataMax",
      splitNumber: axis.splitNumber,
      axisLabel:
        axis.axisLabel && axis.axisLabel.formatter
          ? { formatter: axis.axisLabel.formatter }
          : undefined,
      axisLine:
        Object.keys(axisLine).length > 0 ? axisLine : undefined,
      splitLine:
        Object.keys(splitLine).length > 0 ? splitLine : undefined,
      offset: offsetValue > 0 ? offsetValue : undefined,
    };
  });

  return { yAxis, idIndexMap, sideOffsetMax };
}

function attachAxisBoundsByCategorySeries(
  yAxis: any[],
  seriesConfig: CategorySeriesConfig[]
) {
  const axisValueMap = new Map<string, number[]>();

  seriesConfig.forEach((series) => {
    const axisId = series.axisId ?? DEFAULT_AXIS_ID;
    const values = (series.data || []).filter(
      (value): value is number => typeof value === "number"
    );

    if (!axisValueMap.has(axisId)) {
      axisValueMap.set(axisId, []);
    }

    if (values.length) {
      axisValueMap.get(axisId)?.push(...values);
    }
  });

  return yAxis.map((axis) => {
    // 如果已经设置了 min 或 max（且不是 "dataMin"/"dataMax"），或者没有数据，直接返回
    if (
      (axis.min !== undefined && axis.min !== null && axis.min !== "dataMin") ||
      (axis.max !== undefined && axis.max !== null && axis.max !== "dataMax") ||
      !axisValueMap.has(axis.id)
    ) {
      return axis;
    }

    const values = axisValueMap.get(axis.id) ?? [];
    if (!values.length) {
      return axis;
    }

    // 只有当 min/max 未设置或为 "dataMin"/"dataMax" 时才使用自动计算
    // 保持使用 "dataMin"/"dataMax"，让 ECharts 自动处理，避免下方空白
    return {
      ...axis,
      min: (axis.min === undefined || axis.min === null || axis.min === "dataMin") ? "dataMin" : axis.min,
      max: (axis.max === undefined || axis.max === null || axis.max === "dataMax") ? "dataMax" : axis.max,
    };
  });
}

function buildSeries(
  ctx: ChartBuildContext<CategoryChartType>,
  settings: Partial<CategoryChartSettings>
) {
  const { data, chartType } = ctx;
  const { yAxis, idIndexMap } = resolveAxisConfigs(data);

  const series = data.series.map((seriesItem, seriesIndex) => {
    const type = pickSeriesType(seriesItem.type, chartType, settings);
    const axisId = seriesItem.axisId ?? DEFAULT_AXIS_ID;
    const yAxisIndex = idIndexMap.get(axisId) ?? 0;
    const resolvedColor =
      seriesItem.color ?? DEFAULT_COLORS[seriesIndex % DEFAULT_COLORS.length];

    return {
      id: seriesItem.id,
      name: seriesItem.name,
      type,
      data: seriesItem.data,
      yAxisIndex,
      smooth:
        seriesItem.smooth !== undefined
          ? seriesItem.smooth
          : settings.smoothDefault,
      stack: seriesItem.stack,
      areaStyle:
        type === "line" && (seriesItem.area ?? settings.areaDefault)
          ? {}
          : undefined,
      showSymbol:
        seriesItem.showSymbol !== undefined
          ? seriesItem.showSymbol
          : settings.showSymbolDefault,
      color: resolvedColor,
      itemStyle:
        type === "bar"
          ? { color: resolvedColor }
          : { color: resolvedColor },
      lineStyle:
        type === "line"
          ? { color: resolvedColor, width: 2 }
          : undefined,
      barWidth: type === "bar" ? seriesItem.barWidth : undefined,
      emphasis: { focus: "series" },
    };
  });

  return { series, yAxis };
}

function buildCategoryOption(
  ctx: ChartBuildContext<CategoryChartType>
): EChartsOption {
  const mergedSettings = mergeCategorySettings(ctx.settings);
  const { data } = ctx;
  const { series, yAxis } = buildSeries(ctx, mergedSettings);

  const axisColorByIndex = new Map<number, AxisColorHint>();

  let resolvedYAxis = yAxis;
  if (mergedSettings.synchronizeAxisBounds) {
    resolvedYAxis = attachAxisBoundsByCategorySeries(yAxis, data.series);
  }

  (series as any[]).forEach((item) => {
    const color =
      typeof item.color === "string" ? (item.color as string) : undefined;
    if (!color) {
      return;
    }
    const index = item.yAxisIndex ?? 0;
    const existing = axisColorByIndex.get(index);
    if (!existing || (item.type === "bar" && !existing.isBar)) {
      axisColorByIndex.set(index, { color, isBar: item.type === "bar" });
    }
  });

  if (!axisColorByIndex.has(0) && series.length > 0) {
    const first = series[0] as any;
    const fallbackColor =
      typeof first.color === "string" ? (first.color as string) : DEFAULT_COLORS[0];
    axisColorByIndex.set(0, {
      color: fallbackColor,
      isBar: first.type === "bar",
    });
  }

  const decoratedYAxis = resolvedYAxis.map((axis, index) => {
    // 如果 settings 中设置了 yAxisShow: false，直接关闭所有 Y 轴元素
    if (mergedSettings.yAxisShow === false) {
      return {
        ...axis,
        show: false,
        axisLine: { show: false },
        axisLabel: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
      };
    }

    const hint =
      axisColorByIndex.get(index) ??
      (index < DEFAULT_COLORS.length
        ? { color: DEFAULT_COLORS[index], isBar: false }
        : undefined);
    const axisColor = hint?.color;
    if (!axisColor) {
      return axis;
    }

    const axisWithColor: any = { ...axis };
    axisWithColor.axisLine = {
      ...(axisWithColor.axisLine || {}),
      show: axisWithColor.axisLine?.show !== false,
      lineStyle: {
        ...(axisWithColor.axisLine?.lineStyle || {}),
        color: axisColor,
      },
    };
    axisWithColor.axisTick = {
      ...(axisWithColor.axisTick || {}),
      show: axisWithColor.axisLine?.show !== false,
      length: 12,
      inside: true,
      lineStyle: { color: axisColor, width: 2 },
    };
    axisWithColor.axisLabel = {
      ...(axisWithColor.axisLabel || {}),
      color: axisColor,
    };
    axisWithColor.nameTextStyle = {
      ...(axisWithColor.nameTextStyle || {}),
      color: axisColor,
    };
    if (axisWithColor.splitLine?.show) {
      const rgba = hexToRgba(axisColor, 0.25);
      axisWithColor.splitLine = {
        ...axisWithColor.splitLine,
        lineStyle: {
          ...(axisWithColor.splitLine?.lineStyle || {}),
          color: rgba ?? axisColor,
        },
      };
    }

    return axisWithColor;
  });

  // 根据图例位置计算 grid 偏移量
  const legendConfig = buildLegend(data.legend, mergedSettings.legend);
  const legendPosition = legendConfig.show === false
    ? null
    : (mergedSettings.legend?.position ?? data.legend?.position ?? "top");

  const LEGEND_MARGIN = 16; // 图例离外边距
  const LEGEND_GAP_HORIZONTAL = 24; // 图例在上下时，与绘制区之间的间距
  const LEGEND_GAP_VERTICAL = 16; // 图例在左右时，与绘制区之间的间距
  const LEGEND_HEIGHT = 24; // 水平图例的估算高度
  const LEGEND_WIDTH = 60; // 垂直图例的估算宽度
  const BASE_MARGIN = 16; // 绘制区到另一边的基础边距

  // 计算 grid 偏移量（已删除所有原有内边距）
  // 图例在上：top = 图例外边距(16) + 图例高度(24) + 间距(24) = 64, bottom = 16, left = 16, right = 16
  // 图例在下：top = 16, bottom = 间距(24) + 图例高度(24) + 图例外边距(16) = 64, left = 16, right = 16
  // 图例在左：top = 16, bottom = 16, left = 图例外边距(16) + 图例宽度(60) + 间距(16) = 92, right = 16
  // 图例在右：top = 16, bottom = 16, left = 16, right = 间距(16) + 图例宽度(60) + 图例外边距(16) = 92

  let gridTop: number;
  if (legendPosition === "top") {
    gridTop = LEGEND_MARGIN + LEGEND_HEIGHT + LEGEND_GAP_HORIZONTAL;
  } else if (legendPosition === "bottom") {
    gridTop = BASE_MARGIN;
  } else if (legendPosition === "left" || legendPosition === "right") {
    // 图例在左右时，上下边距为16
    gridTop = BASE_MARGIN;
  } else {
    gridTop = 40;
  }

  let gridBottom: number;
  if (legendPosition === "bottom") {
    gridBottom = LEGEND_GAP_HORIZONTAL + LEGEND_HEIGHT + LEGEND_MARGIN;
  } else if (legendPosition === "top") {
    gridBottom = BASE_MARGIN;
  } else if (legendPosition === "left" || legendPosition === "right") {
    // 图例在左右时，上下边距为16
    gridBottom = BASE_MARGIN;
  } else {
    gridBottom = 40;
  }

  let gridLeft: number;
  let gridRight: number;
  if (legendPosition === "top" || legendPosition === "bottom") {
    // 图例在上或下时，左右边距为16（删除所有原有内边距）
    gridLeft = BASE_MARGIN;
    gridRight = BASE_MARGIN;
  } else {
    // 图例在左右时，删除原有内边距，只保留图例相关空间
    gridLeft = (legendPosition === "left" ? LEGEND_MARGIN + LEGEND_WIDTH + LEGEND_GAP_VERTICAL : BASE_MARGIN);
    gridRight = (legendPosition === "right" ? LEGEND_GAP_VERTICAL + LEGEND_WIDTH + LEGEND_MARGIN : BASE_MARGIN);
  }

  // 从 settings 中读取 xAxis 配置（如果存在）
  const settingsXAxis = (mergedSettings as any).xAxis;
  const shouldShowXAxis = mergedSettings.xAxisShow !== false;
  
  // 构建基础 x 轴配置
  const baseXAxis: any = {
    type: "category" as const,
    data: data.categories,
    boundaryGap: series.some((s) => s.type === "bar"),
    show: shouldShowXAxis,
    splitLine: { show: false }, // 关闭 X 轴分割线
  };

  // 如果 x 轴应该显示，设置默认的 axisLine、axisLabel、axisTick
  if (shouldShowXAxis) {
    baseXAxis.axisLine = undefined;
    baseXAxis.axisLabel = undefined;
    baseXAxis.axisTick = undefined;
  } else {
    baseXAxis.axisLine = { show: false };
    baseXAxis.axisLabel = { show: false };
    baseXAxis.axisTick = { show: false };
  }

  // 如果 settings 中有 xAxis 配置，合并到基础配置中
  // 注意：xAxis.data 始终使用 data.categories（数据源），而不是 settings 中的 data
  const finalXAxis = settingsXAxis
    ? [
        {
          ...baseXAxis,
          ...settingsXAxis,
          // 始终使用 data.categories 作为 x 轴数据源
          data: data.categories,
          // 深度合并 axisLabel 配置
          axisLabel: settingsXAxis.axisLabel
            ? {
                ...(baseXAxis.axisLabel || {}),
                ...settingsXAxis.axisLabel,
              }
            : baseXAxis.axisLabel,
          // 深度合并 axisLine 配置
          axisLine: settingsXAxis.axisLine
            ? {
                ...(baseXAxis.axisLine || {}),
                ...settingsXAxis.axisLine,
              }
            : baseXAxis.axisLine,
          // 深度合并 axisTick 配置
          axisTick: settingsXAxis.axisTick
            ? {
                ...(baseXAxis.axisTick || {}),
                ...settingsXAxis.axisTick,
              }
            : baseXAxis.axisTick,
        },
      ]
    : [baseXAxis];

  const option: EChartsOption = {
    textStyle: {
      fontFamily:
        '"Noto Sans SC", "PingFang SC", "Microsoft YaHei", "Helvetica Neue", sans-serif',
    },
    grid: {
      top: gridTop,
      left: gridLeft,
      right: gridRight,
      bottom: gridBottom,
      containLabel: true,
    },
    tooltip: buildTooltip(data.tooltip, mergedSettings.tooltip),
    legend: legendConfig,
    xAxis: finalXAxis,
    yAxis: decoratedYAxis,
    series,
  };

  const dataZoom = buildDataZoom(data.dataZoom, mergedSettings.dataZoom);
  if (dataZoom) {
    option.dataZoom = dataZoom;
  }

  return option;
}

export function createCategoryChartAdapter(
  chartType: CategoryChartType
): ChartAdapter<CategoryChartType> {
  return {
    type: chartType,
    buildOption: (ctx) =>
      buildCategoryOption({
        ...ctx,
        chartType,
      }),
    getDefaultSettings: () => ({
      legend: { show: true },
      tooltip: { enable: true, trigger: "axis" },
      dataZoom: { enable: false, start: 0, end: 100 },
      areaDefault: false,
      smoothDefault: chartType === "line",
      showSymbolDefault: chartType === "line",
      synchronizeAxisBounds: false,
    }),
  };
}

